import { buildFeedbackMessage } from '../config/captureQuality';

const VIEW_THRESHOLDS = {
  frente: {
    minAreaWarning: 0.22,
    minAreaError: 0.14,
    minHeightWarning: 0.3,
    minHeightError: 0.24,
    maxCenterOffset: 0.16,
    edgeMargin: 0.01,
    minEdgeTouchesForCut: 2,
    minAreaForEdgeCutError: 0.72,
    horizontalCropEdgeMargin: 0.03,
    horizontalCropDiffMin: 0.06,
    minAreaForSoftCrop: 0.3,
    allowHorizontalSoftCrop: true,
    profileAltClassWarningMin: 0.14,
    profileTallHeightMin: 0.64,
    profileTallAspectMax: 1.5,
    profileTallAltClassMin: 0.1,
    profileWideCropWidthMin: 0.98,
    profileWideCropHeightMax: 0.38,
    profileWideCropHorizontalMarginMax: 0.03,
    profileSquareAspectMax: 1.15,
    profileSquareAreaMin: 0.78,
    profileSquareHeightMin: 0.8
  },
  posterior: {
    minAreaWarning: 0.22,
    minAreaError: 0.14,
    minHeightWarning: 0.3,
    minHeightError: 0.24,
    maxCenterOffset: 0.16,
    edgeMargin: 0.01,
    minEdgeTouchesForCut: 2,
    minAreaForEdgeCutError: 0.72,
    horizontalCropEdgeMargin: 0.03,
    horizontalCropDiffMin: 0.06,
    minAreaForSoftCrop: 0.3,
    allowHorizontalSoftCrop: true,
    profileAltClassWarningMin: 0.14,
    profileTallHeightMin: 0.64,
    profileTallAspectMax: 1.5,
    profileTallAltClassMin: 0.1,
    profileWideCropWidthMin: 0.98,
    profileWideCropHeightMax: 0.38,
    profileWideCropHorizontalMarginMax: 0.03,
    profileSquareAspectMax: 1.15,
    profileSquareAreaMin: 0.78,
    profileSquareHeightMin: 0.8
  },
  perfil: {
    minAreaWarning: 0.18,
    minAreaError: 0.1,
    minHeightWarning: 0.3,
    minHeightError: 0.24,
    maxCenterOffset: 0.2,
    edgeMargin: 0.01,
    minEdgeTouchesForCut: 2,
    minAreaForEdgeCutError: 0.68,
    horizontalCropEdgeMargin: 0.03,
    horizontalCropDiffMin: 0.06,
    minAreaForSoftCrop: 0.3,
    allowHorizontalSoftCrop: true,
    profileAltClassWarningMin: 0.14,
    profileTallHeightMin: 0.64,
    profileTallAspectMax: 1.5,
    profileTallAltClassMin: 0.1,
    profileWideCropWidthMin: 0.98,
    profileWideCropHeightMax: 0.38,
    profileWideCropHorizontalMarginMax: 0.03,
    profileSquareAspectMax: 1.15,
    profileSquareAreaMin: 0.78,
    profileSquareHeightMin: 0.8
  }
};

const DEFAULT_VIEW = 'frente';
const MIN_ANALYZABLE_SCORE = 0.35;

const uniqueMessages = (messages) => Array.from(new Set(messages.filter(Boolean)));

const normalizeViewKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'trasero' || normalized === 'posterior' || normalized === 'vista posterior') return 'posterior';
  if (normalized.includes('perfil')) return 'perfil';
  if (normalized.includes('frente')) return 'frente';
  return DEFAULT_VIEW;
};

const getEdgeTouchCount = (detection, edgeMargin) => {
  let count = 0;
  if (detection.xMin <= edgeMargin) count += 1;
  if (detection.yMin <= edgeMargin) count += 1;
  if (detection.xMax >= 1 - edgeMargin) count += 1;
  if (detection.yMax >= 1 - edgeMargin) count += 1;
  return count;
};

const getMargins = (detection) => ({
  left: detection.xMin,
  right: 1 - detection.xMax,
  top: detection.yMin,
  bottom: 1 - detection.yMax
});

const getOrientationProfile = (imageAspectRatio) => {
  if (!Number.isFinite(imageAspectRatio) || imageAspectRatio <= 0) return 'unknown';
  return imageAspectRatio < 1 ? 'portrait' : 'landscape';
};

const getEffectiveThresholds = (thresholds, orientationProfile, viewKey) => {
  if (orientationProfile !== 'portrait') {
    if (viewKey === 'perfil') {
      return {
        ...thresholds,
        allowHorizontalSoftCrop: false
      };
    }
    return thresholds;
  }

  if (viewKey === 'perfil') {
    return {
      ...thresholds,
      minAreaWarning: thresholds.minAreaWarning - 0.02,
      minAreaError: thresholds.minAreaError - 0.02,
      minHeightWarning: thresholds.minHeightWarning - 0.02,
      minHeightError: thresholds.minHeightError - 0.02,
      horizontalCropDiffMin: thresholds.horizontalCropDiffMin + 0.04
    };
  }

  return {
    ...thresholds,
    minAreaWarning: thresholds.minAreaWarning - 0.02,
    minAreaError: thresholds.minAreaError - 0.02,
    horizontalCropDiffMin: thresholds.horizontalCropDiffMin + 0.02
  };
};

const isLikelySingleEdgeCrop = (detection, thresholds) => {
  if (!thresholds.allowHorizontalSoftCrop) return false;
  const margins = getMargins(detection);
  const horizontalAsymmetry = Math.abs(margins.left - margins.right);

  const horizontalCrop = (
    (margins.left <= thresholds.horizontalCropEdgeMargin || margins.right <= thresholds.horizontalCropEdgeMargin)
    && horizontalAsymmetry >= thresholds.horizontalCropDiffMin
  );

  return detection.areaRatio >= thresholds.minAreaForSoftCrop && horizontalCrop;
};

const getClassScore = (classScores, classId) => {
  if (!classScores) return 0;
  const value = classScores[classId] ?? classScores[String(classId)];
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

const getProfilePoseSignals = ({ detection, classScores, thresholds }) => {
  const alternateClassMax = Math.max(
    getClassScore(classScores, 1),
    getClassScore(classScores, 3)
  );
  const horizontalMarginTotal = detection.xMin + (1 - detection.xMax);
  const boxAspectRatio = detection.widthRatio / Math.max(detection.heightRatio, 1e-6);
  const flags = [];

  if (alternateClassMax >= thresholds.profileAltClassWarningMin && boxAspectRatio <= 1.75) {
    flags.push('ambiguous_pose');
  }

  if (
    detection.heightRatio >= thresholds.profileTallHeightMin
    && boxAspectRatio <= thresholds.profileTallAspectMax
    && alternateClassMax >= thresholds.profileTallAltClassMin
  ) {
    flags.push('too_tall_for_profile');
  }

  if (
    detection.widthRatio >= thresholds.profileWideCropWidthMin
    && detection.heightRatio <= thresholds.profileWideCropHeightMax
    && horizontalMarginTotal <= thresholds.profileWideCropHorizontalMarginMax
  ) {
    flags.push('overwide_crop');
  }

  if (
    boxAspectRatio <= thresholds.profileSquareAspectMax
    && detection.areaRatio >= thresholds.profileSquareAreaMin
    && detection.heightRatio >= thresholds.profileSquareHeightMin
  ) {
    flags.push('near_square_fullframe');
  }

  return {
    flags,
    alternateClassMax,
    horizontalMarginTotal,
    boxAspectRatio
  };
};

export const analyzeVehicleDetectionBox = ({ detection, detectedView, expectedView, imageAspectRatio, classScores }) => {
  if (!detection || !Number.isFinite(detection.score) || detection.score < MIN_ANALYZABLE_SCORE) {
    return null;
  }

  const viewKey = normalizeViewKey(detectedView || expectedView);
  const orientationProfile = getOrientationProfile(imageAspectRatio);
  const thresholds = getEffectiveThresholds(
    VIEW_THRESHOLDS[viewKey] || VIEW_THRESHOLDS[DEFAULT_VIEW],
    orientationProfile,
    viewKey
  );
  const errors = [];
  const warnings = [];
  const edgeTouchCount = getEdgeTouchCount(detection, thresholds.edgeMargin);
  const tooFarByArea = detection.areaRatio < thresholds.minAreaError
    ? 'error'
    : detection.areaRatio < thresholds.minAreaWarning
      ? 'warning'
      : null;
  const tooFarByHeight = detection.heightRatio < thresholds.minHeightError
    ? 'error'
    : detection.heightRatio < thresholds.minHeightWarning
      ? 'warning'
      : null;
  const tooFarSignals = [tooFarByArea, tooFarByHeight].filter(Boolean);
  const explicitTooFar = orientationProfile === 'portrait'
    ? (tooFarByHeight || tooFarByArea)
    : tooFarByArea;

  if (explicitTooFar === 'error' || tooFarSignals.filter((signal) => signal === 'error').length >= 2) {
    errors.push('Estás muy lejos');
    warnings.push('Acércate al vehículo');
  } else if (explicitTooFar === 'warning' || tooFarSignals.length >= 2) {
    warnings.push('Acércate un poco más');
  }

  if (edgeTouchCount >= thresholds.minEdgeTouchesForCut && detection.areaRatio >= thresholds.minAreaForEdgeCutError) {
    errors.push('Falta parte del vehículo');
  } else if (isLikelySingleEdgeCrop(detection, thresholds)) {
    warnings.push('Falta parte del vehículo');
  }

  const profilePoseSignals = viewKey === 'perfil'
    ? getProfilePoseSignals({ detection, classScores, thresholds })
    : { flags: [], alternateClassMax: 0, horizontalMarginTotal: 0, boxAspectRatio: detection.widthRatio / Math.max(detection.heightRatio, 1e-6) };

  if (profilePoseSignals.flags.length > 0) {
    errors.push(buildFeedbackMessage('profile_pose_invalid'));
  }

  const offsetX = Math.abs(detection.centerX - 0.5);
  const offsetY = Math.abs(detection.centerY - 0.5);
  const centerOffset = Math.max(offsetX, offsetY);

  if (centerOffset > thresholds.maxCenterOffset) {
    warnings.push('Centra el auto en la imagen');
  }

  return {
    viewKey,
    metrics: {
      orientationProfile,
      areaRatio: detection.areaRatio,
      widthRatio: detection.widthRatio,
      heightRatio: detection.heightRatio,
      boxAspectRatio: profilePoseSignals.boxAspectRatio,
      centerOffsetRatio: centerOffset,
      edgeTouchCount,
      touchesEdge: edgeTouchCount > 0,
      likelySingleEdgeCrop: isLikelySingleEdgeCrop(detection, thresholds),
      alternateClassMax: profilePoseSignals.alternateClassMax,
      horizontalMarginTotal: profilePoseSignals.horizontalMarginTotal,
      profilePoseFlags: profilePoseSignals.flags
    },
    errors: uniqueMessages(errors),
    warnings: uniqueMessages(warnings)
  };
};
